// "Download" on a chart card and in its fullscreen view: hands the reader the
// chart's own result as a plain CSV file.
//
// It is an entirely CLIENT-side save — the result is already in hand (the same
// `ChartResult` the card is rendering), so there is no endpoint, no round trip,
// and nothing for request supersession to get wrong. A reader can never be
// handed a file computed under a filter state they have since moved off,
// because the bytes come from the number they are looking at.
//
// The serialization and the filename are `lib/csv.ts` (pure, tested — this
// app's rule for anything that isn't a render); `downloadBlob` is
// @bi/shared's one object-URL dance. What is left here is the button.
import { downloadBlob } from "@bi/shared";
import type { ChartResult, ChartSpec } from "@bi/shared/types";

import { CSV_MIME_TYPE, UTF8_BOM, csvFilename, toCsv } from "../lib/csv";
import { DownloadIcon } from "./icons";

export interface ChartDownloadButtonProps {
  chart: ChartSpec;
  result: ChartResult;
  /** The host surface's own icon-button class (a card head and a modal head
   *  style their chrome differently). Passed in rather than branched on here so
   *  this component owns no layout. */
  className: string;
  iconSize?: number;
}

export function ChartDownloadButton({
  chart,
  result,
  className,
  iconSize = 16,
}: ChartDownloadButtonProps) {
  const save = () => {
    const csv = toCsv(result.columns, result.rows);
    // The BOM is what makes Excel read the file as UTF-8 instead of the system
    // codepage; without it any non-ASCII seller name or brief title arrives
    // mojibaked. See UTF8_BOM's own note.
    const blob = new Blob([UTF8_BOM, csv], { type: CSV_MIME_TYPE });
    downloadBlob(blob, csvFilename(chart.slice_name, result.computed_at));
  };

  return (
    <button
      type="button"
      className={className}
      onClick={save}
      // The chart's name is in the accessible name because a card head holds
      // two identical-looking icon buttons and a screen reader announces this
      // one out of the context that would otherwise disambiguate it.
      aria-label={`Download ${chart.slice_name} as CSV`}
      title="Download CSV"
    >
      <DownloadIcon size={iconSize} />
    </button>
  );
}
